// Geometría de conexiones: posición de pines en el mundo, piezas sobre la protoboard y
// enlaces implícitos (patas insertadas en agujeros) que se suman a los cables.
import { getDef, getPins, getFootprint, resolveProps, PITCH } from './parts/index.js';
import { BB_H } from './parts/breadboard.js';

export function rotXZ(x, z, rotDeg) {
  const r = (rotDeg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [x * c + z * s, -x * s + z * c];
}

/** Punto local de una pieza → mundo (sin altura). */
export function toWorld(part, x, z) {
  const [rx, rz] = rotXZ(x, z, part.rot || 0);
  return [part.x + rx, part.z + rz];
}

/** Mundo → local de una pieza. */
export function toLocal(part, wx, wz) {
  return rotXZ(wx - part.x, wz - part.z, -(part.rot || 0));
}

export function partProps(part) {
  if (!part._resolved || part._resolvedFrom !== part.props) {
    part._resolved = resolveProps(part.type, part.props);
    part._resolvedFrom = part.props;
  }
  return part._resolved;
}

/** ¿Está el punto (mundo) sobre la superficie de la protoboard? */
export function overBreadboard(bb, wx, wz, margin = 0) {
  const [lx, lz] = toLocal(bb, wx, wz);
  const f = getFootprint(bb.type, partProps(bb));
  return Math.abs(lx) <= f.w / 2 + margin && Math.abs(lz) <= f.d / 2 + margin;
}

export function breadboardAt(project, wx, wz, exceptId = null) {
  for (let i = project.parts.length - 1; i >= 0; i--) {
    const p = project.parts[i];
    if (p.id === exceptId || getDef(p.type).kind !== 'breadboard') continue;
    if (overBreadboard(p, wx, wz)) return p;
  }
  return null;
}

/**
 * Calcula la altura de cada pieza y las inserciones en protoboards.
 * Devuelve { y: Map(id → altura), inserted: Map(id → bbId), links: [[{part,pin},{part,pin}]], misaligned: Set(id) }
 */
export function computeLayout(project) {
  const y = new Map();
  const inserted = new Map();
  const misaligned = new Set();
  const links = [];
  const boards = project.parts.filter((p) => getDef(p.type).kind === 'breadboard');
  for (const part of project.parts) {
    const def = getDef(part.type);
    if (def.kind === 'breadboard') {
      y.set(part.id, 0);
      continue;
    }
    const bb = boards.length ? breadboardAt(project, part.x, part.z) : null;
    y.set(part.id, bb ? BB_H : 0);
    if (!bb || !def.insertable) continue;
    const props = partProps(part);
    const bbProps = partProps(bb);
    const bbDef = getDef(bb.type);
    const pins = getPins(part.type, props).filter((p) => p.insert);
    const found = [];
    for (const pin of pins) {
      const [wx, wz] = toWorld(part, pin.x, pin.z);
      const [lx, lz] = toLocal(bb, wx, wz);
      const hole = bbDef.holeAt(bbProps, lx, lz, 0.6);
      if (!hole) break;
      found.push([pin.name, hole]);
    }
    if (found.length === pins.length && pins.length) {
      inserted.set(part.id, bb.id);
      for (const [pin, hole] of found) links.push([{ part: part.id, pin }, { part: bb.id, pin: hole }]);
    } else if (pins.length) {
      misaligned.add(part.id);
    }
  }
  return { y, inserted, links, misaligned };
}

/** Proyecto en la forma que necesita el simulador. */
export function simProject(project, layout = computeLayout(project)) {
  const ids = new Set(project.parts.map((p) => p.id));
  const links = project.wires.filter((w) => ids.has(w.a.part) && ids.has(w.b.part)).map((w) => [w.a, w.b]);
  return {
    parts: project.parts.map((p) => ({ id: p.id, type: p.type, props: { ...p.props } })),
    links: [...links, ...layout.links],
  };
}

/**
 * Ajusta la posición de una pieza que se arrastra: sobre una protoboard alinea sus patas con
 * los agujeros; en la mesa redondea a medio paso (1.27 mm).
 */
export function snapPosition(project, part, x, z) {
  const def = getDef(part.type);
  const half = PITCH / 2;
  const bb = def.kind === 'breadboard' ? null : breadboardAt(project, x, z, part.id);
  if (bb && def.insertable) {
    const pins = getPins(part.type, partProps(part)).filter((p) => p.insert);
    if (pins.length) {
      const probe = { ...part, x, z };
      const [wx, wz] = toWorld(probe, pins[0].x, pins[0].z);
      const [lx, lz] = toLocal(bb, wx, wz);
      // La rejilla de la protoboard: columnas en múltiplos de PITCH desde su centro (más medio
      // paso si el número de columnas es par) y filas centradas en el canal.
      const bbProps = partProps(bb);
      const cols = getDef(bb.type).holeAt ? bbCols(bbProps) : 30;
      const offX = cols % 2 === 0 ? half : 0;
      const gx = Math.round((lx - offX) / PITCH) * PITCH + offX;
      const gz = Math.round((lz - half) / PITCH) * PITCH + half;
      const [dwx, dwz] = rotDelta(bb, gx - lx, gz - lz);
      return { x: x + dwx, z: z + dwz };
    }
  }
  return { x: Math.round(x / half) * half, z: Math.round(z / half) * half };
}

function bbCols(props) {
  return { full: 63, half: 30, mini: 17 }[props.size] || 30;
}

function rotDelta(part, dx, dz) {
  return rotXZ(dx, dz, part.rot || 0);
}

/** Pines (con posición en el mundo) de una pieza. */
export function worldPins(part, layoutY = 0) {
  const props = partProps(part);
  return getPins(part.type, props).map((p) => {
    const [wx, wz] = toWorld(part, p.x, p.z);
    const dir = p.dir ? rotDir(p.dir, part.rot || 0) : [0, 1, 0];
    return { ...p, wx, wy: layoutY + p.y, wz, wdir: dir };
  });
}

export function rotDir(dir, rot) {
  const [x, z] = rotXZ(dir[0], dir[2], rot);
  return [x, dir[1], z];
}
